import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  AlertTriangle, 
  Flame, 
  Eye, 
  HardHat, 
  ArrowRight,
  CheckCircle,
  Star,
  Globe,
  Zap,
  Wind,
  Thermometer
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer } from '../../utils/cn';

const Hazardous = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Slider state for hero section
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const sliderImages = [
    '/divisions/Hazardous/industrial-complex-at-night-2023-11-27-05-26-16-utc.jpg',
    '/divisions/Oil and Gas/oil-refinery-2023-11-27-05-26-16-utc.jpg',
    '/divisions/Hazardous/chemical-plant-for-production-of-ammonia-on-night-2023-11-27-05-26-16-utc.jpg',
    '/divisions/Oil and Gas/oil-drilling-rig-at-sunset-2023-11-27-05-26-16-utc.jpg',
    '/divisions/Hazardous/hazard-warning-signs-industry-2023-11-27-05-26-16-utc.jpg'
  ];

  useEffect(() => {
    // Ensure page starts from top on component mount
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === sliderImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [sliderImages.length]);

  const products = [
    {
      icon: Shield,
      title: 'Explosion-Proof Equipment',
      description: 'Certified explosion-proof electrical equipment for hazardous environments',
      features: ['Ex d Flameproof', 'Ex e Increased Safety', 'Ex i Intrinsic Safety', 'Ex n Non-Sparking'],
      image: '/divisions/Hazardous/industrial-complex-at-night-2023-11-27-05-34-39-utc.jpg'
    },
    {
      icon: Flame,
      title: 'Fire & Gas Detection',
      description: 'Advanced fire and gas detection systems for early warning',
      features: ['Gas Detectors', 'Flame Detectors', 'Fire Alarm Systems', 'Emergency Shutdown'],
      image: '/divisions/Oil and Gas/oil-drilling-rig-construction-site-2023-11-27-05-36-31-utc.jpg'
    },
    {
      icon: Eye,
      title: 'Safety Monitoring',
      description: 'Comprehensive safety monitoring and control systems',
      features: ['Safety PLCs', 'Emergency Stop Systems', 'Safety Barriers', 'Monitoring Panels'],
      image: '/divisions/Hazardous/chemical-plant-for-production-of-ammonia-on-night-2023-11-27-05-18-08-utc.jpg'
    },
    {
      icon: HardHat,
      title: 'Personal Safety Equipment',
      description: 'Personal protective equipment for hazardous area workers',
      features: ['Gas Monitors', 'Safety Helmets', 'Protective Clothing', 'Emergency Equipment'],
      image: '/divisions/Hazardous/hazard-warning-signs-industry-2023-11-27-05-26-16-utc.jpg'
    },
    {
      icon: Zap,
      title: 'Intrinsically Safe Devices',
      description: 'Intrinsically safe instruments and communication devices',
      features: ['IS Instruments', 'IS Communication', 'IS Lighting', 'IS Control Devices'],
      image: '/divisions/Oil and Gas/oil-refinery-at-twilight-chao-phraya-river-bangk-2023-11-27-05-09-09-utc.jpg'
    },
    {
      icon: Wind,
      title: 'Ventilation Systems',
      description: 'Specialized ventilation and air purification systems',
      features: ['Explosion-Proof Fans', 'Air Purification', 'Pressurization Systems', 'Emergency Ventilation'],
      image: '/divisions/Hazardous/liquid-natural-gas-globe-shape-containers-2023-11-27-05-37-11-utc.jpg'
    }
  ];

  const certifications = [
    {
      title: 'ATEX Certification',
      description: 'European conformity for equipment in explosive atmospheres',
      zones: ['Zone 0, 1, 2 (Gas)', 'Zone 20, 21, 22 (Dust)']
    },
    {
      title: 'IECEx Certification',
      description: 'International electrotechnical commission explosive certification',
      zones: ['Class I, II, III', 'Division 1 & 2']
    },
    {
      title: 'UL/CSA Standards',
      description: 'North American safety standards for hazardous locations',
      zones: ['Class I, II, III', 'Division 1 & 2']
    },
    {
      title: 'PESO Approval',
      description: 'Petroleum and Explosives Safety Organization approval for India',
      zones: ['Zone Classification', 'Temperature Rating']
    }
  ];

  const industries = [
    {
      title: 'Oil & Gas',
      description: 'Upstream, midstream, and downstream oil and gas operations',
      applications: ['Refineries', 'Petrochemical Plants', 'Offshore Platforms', 'Pipeline Stations'],
      icon: Flame
    },
    {
      title: 'Chemical Processing',
      description: 'Chemical manufacturing and processing facilities',
      applications: ['Chemical Plants', 'Pharmaceutical', 'Specialty Chemicals', 'Fertilizer Plants'],
      icon: Thermometer
    },
    {
      title: 'Mining Operations',
      description: 'Underground and surface mining operations',
      applications: ['Coal Mining', 'Metal Mining', 'Quarrying', 'Mineral Processing'],
      icon: HardHat
    },
    {
      title: 'Power Generation',
      description: 'Power plants and energy generation facilities',
      applications: ['Thermal Power', 'Nuclear Plants', 'Renewable Energy', 'Substations'],
      icon: Zap
    }
  ];

  const brands = [
    { name: 'ABB', logo: '/Suppliers logo/ABB.png' },
    { name: 'Schneider Electric', logo: '/Suppliers logo/schneider-logo.png' },
    { name: 'Siemens', logo: '/Suppliers logo/siemens.png' },
    { name: 'Eaton', logo: '/Suppliers logo/Eaton.jpg' },
    { name: 'Honeywell', logo: '/Suppliers logo/Honeywell-Logo.png' },
    { name: 'GE', logo: '/Suppliers logo/GE.png' },
    { name: 'Emerson', logo: '/Suppliers logo/emerson.png' },
    { name: 'Phoenix Contact', logo: '/Suppliers logo/phoenix-contact-logo.png' },
    { name: 'Fluke', logo: '/Suppliers logo/Fluke.png' },
    { name: 'Moxa', logo: '/Suppliers logo/Moxa.png' },
    { name: 'Mean Well', logo: '/Suppliers logo/Mean-Well.png' },
    { name: 'Legrand', logo: '/Suppliers logo/legrand-logo.png' },
    { name: 'Mitsubishi Electric', logo: '/Suppliers logo/mitsubishi-electric.png' }
  ];

  const services = [
    {
      title: 'Hazard Assessment',
      description: 'Comprehensive hazard and risk assessment services'
    },
    {
      title: 'Zone Classification',
      description: 'Professional area classification and documentation'
    },
    {
      title: 'Installation',
      description: 'Certified installation by trained technicians'
    },
    {
      title: 'Maintenance',
      description: 'Specialized maintenance and inspection programs'
    }
  ];

  const safetyFeatures = [
    {
      title: 'Temperature Control',
      description: 'Advanced temperature monitoring and control for safe operation',
      icon: Thermometer
    },
    {
      title: 'Pressure Management',
      description: 'Pressure relief and monitoring systems for hazardous areas',
      icon: Wind
    },
    {
      title: 'Emergency Response',
      description: 'Rapid emergency response and shutdown systems',
      icon: AlertTriangle
    },
    {
      title: 'Continuous Monitoring',
      description: '24/7 monitoring of critical safety parameters',
      icon: Eye
    }
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="section-padding solid-bg text-white relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('/divisions/Hazardous/industrial-complex-at-night-2023-11-27-05-34-39-utc.jpg')`
          }}
        />
        

        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-accent rounded-full opacity-30 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        <div className="container-max relative z-10">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            {...fadeInUp}
          >
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                {t('divisions.hazardous.title')}
              </h1>
              <p className="text-xl md:text-xl mb-8 leading-relaxed">
                We offer specialized solutions for hazardous areas, ensuring safety and 
                compliance in environments where risks are heightened. Our range of 
                commercial hazardous area products includes rugged and reliable equipment 
                designed to operate safely under challenging conditions. From explosion-proof 
                lighting and intrinsically safe communication devices to specialized enclosures 
                and controls, our products are engineered to meet the strictest industry 
                standards. With our expertise and high-quality offerings, we provide peace 
                of mind and protection for your operations, helping you maintain safety and 
                efficiency in the most demanding environments. 
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" variant="secondary">
                    Get Quote
                    <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                  </Button>
                </Link>
                <Link to="/hazardousdivisioncompany">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-charcoal">
                    Our Partner Companies
                  </Button>
                </Link>
              </div>
            </div>
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 relative overflow-hidden">
           
                <div className="absolute inset-0 rounded-2xl overflow-hidden">
                  {sliderImages.map((image, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 h-full bg-cover bg-center opacity-30 transition-opacity duration-1000 ${
                        index === currentImageIndex ? 'opacity-30' : 'opacity-0'
                      }`}
                      style={{backgroundImage: `url('${image}')`}}
                    />
                  ))}
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl" />
                
                <div className="relative z-10">
                  <Shield className="w-24 h-24 text-accent mx-auto mb-6" />
                  <div className="text-center">
                    <h3 className="text-2xl font-semibold mb-4">Safety First</h3>
                    <p className="text-lg opacity-90">
                       Providing Inclusive Hazardous 
                       Area Products and Services
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Hazardous Infrastructure Gallery */}
      <section className="section-padding bg-soft-gray">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Hazardous Area Infrastructure
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Explore our comprehensive range of safety solutions for hazardous environments across various industries.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                image: '/divisions/Hazardous/chemical-plant-for-production-of-ammonia-on-night-2023-11-27-05-18-08-utc.jpg',
                title: 'Chemical Processing Plants',
                description: 'Advanced safety systems for chemical manufacturing and processing facilities'
              },
              {
                image: '/divisions/Oil and Gas/oil-refinery-at-twilight-chao-phraya-river-bangk-2023-11-27-05-09-09-utc.jpg',
                title: 'Oil & Gas Refineries',
                description: 'Explosion-proof equipment for petroleum refining and processing operations'
              },
              {
                image: '/divisions/Hazardous/fpso-tanker-vessel-near-oil-platform-rig-at-night-2023-11-27-04-59-12-utc.jpg',
                title: 'Offshore Platforms',
                description: 'Marine-grade hazardous area equipment for offshore oil and gas operations'
              },
              {
                image: '/divisions/Hazardous/liquid-natural-gas-globe-shape-containers-2023-11-27-05-37-11-utc.jpg',
                title: 'LNG Facilities',
                description: 'Specialized safety systems for liquefied natural gas storage and handling'
              },
              {
                image: '/divisions/Hazardous/industrial-factory-2023-11-27-05-17-09-utc.jpg',
                title: 'Industrial Manufacturing',
                description: 'Comprehensive safety solutions for heavy industrial manufacturing environments'
              },
              {
                image: '/divisions/Oil and Gas/elevated-night-view-of-an-oil-refinery-complex-2024-07-29-20-20-48-utc.JPG',
                title: 'Refinery Complexes',
                description: 'Integrated safety systems for large-scale petroleum refining complexes'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-sm opacity-90">{item.description}</p>
                  </div>
                </div>
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-300" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Hazardous Area Products
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Comprehensive range of certified explosion-proof equipment for hazardous environments.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {products.map((product, index) => {
              const Icon = product.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="group"
                >
                  <Card className="h-full hover:shadow-2xl transition-all duration-500 overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/20 to-transparent" />
                      <div className="absolute top-4 right-4">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-lg">{product.title}</CardTitle>
                      <CardDescription>{product.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {product.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center text-sm text-slate">
                            <CheckCircle className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Safety Features */}
      <section className="section-padding bg-soft-gray">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Advanced Safety Features
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              State-of-the-art safety features designed to protect personnel and equipment.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {safetyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-charcoal mb-3">{feature.title}</h3>
                  <p className="text-slate">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section */}


      {/* Industries Section */}
      <section 
        className="section-padding relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/divisions/Oil and Gas/elevated-night-view-of-an-oil-refinery-complex-2024-07-29-20-20-48-utc.JPG')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Geometric Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="industriesGrid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#industriesGrid)" />
            </svg>
          </div>
        </div>

        <div className="container-max relative z-10">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Specialized solutions for high-risk industries requiring explosion-proof equipment.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group"
                >
                  <Card className="h-full bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-500">
                    <CardHeader>
                      <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <CardTitle className="text-xl text-white">{industry.title}</CardTitle>
                      <CardDescription className="text-gray-200">{industry.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2">
                        {industry.applications.map((app, appIndex) => (
                          <div key={appIndex} className="flex items-center text-sm text-gray-200">
                            <CheckCircle className="w-3 h-3 text-primary mr-2 flex-shrink-0" />
                            {app}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Brands Global Technology Distributes
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              We represent leading manufacturers of explosion-proof and safety equipment.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {brands.map((brand, index) => (
              <motion.div
                key={index}
                className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                variants={fadeInUp}
              >
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="w-16 h-16 object-contain mx-auto mb-3"
                  onError={(e) => {
                    e.target.src = '/placeholder-logo.svg';
                  }}
                />
                <h3 className="font-semibold text-charcoal mb-1">{brand.name}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        className="section-padding relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url('/divisions/Hazardous/industrial-factory-2023-11-27-05-17-09-utc.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >

        <div className="container-max relative z-10">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Specialized Services
            </h2>
            <p className="text-xl max-w-3xl mx-auto text-gray-200">
              Expert services for hazardous area classification, installation, and maintenance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="text-center group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{service.title}</h3>
                <p className="text-gray-200">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center bg-gradient-to-r from-soft-gray to-white rounded-2xl p-12"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Ensure Maximum Safety
            </h2>
            <p className="text-xl text-slate mb-8 max-w-2xl mx-auto">
              Contact our hazardous area specialists to discuss your safety requirements and compliance needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="w-full sm:w-auto">
                  Safety Consultation
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              </Link>
              <Link to="/industries">
                <Button variant="outline" size="lg" className="w-full sm:w-auto">
                  View All Industries
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Hazardous;